import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { signIn, signOut, getCurrentUser, fetchUserAttributes, confirmSignIn, fetchAuthSession, updatePassword } from '@aws-amplify/auth';
import { UserRole } from '@taxassist/api-client';
import { setSentryUser, clearSentryUser } from '../config/sentry';

export interface User {
  userId: string;
  email: string;
  given_name?: string;
  family_name?: string;
  role?: string;
  groups?: string[];
  firmIds?: string[];
  invitedBy?: string;
}

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  requiresNewPassword: boolean;
  tempUsername: string | null;
  sessionExpired: boolean;
  requiresPasswordReset: boolean;
  resetUsername: string | null;
}

export interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  confirmNewPassword: (newPassword: string, userAttributes?: Record<string, string>) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  handleSessionExpired: () => void;
}

type AuthContextType = AuthState & AuthActions;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Map Cognito groups to user roles - return the group name, not the display name
// This ensures compatibility with API client types and permission utilities
function mapGroupsToRole(groups: string[]): string | undefined {

  // Check for admin role first (highest privilege) - use UserRole constants
  if (groups.includes(UserRole.admin)) {
    return UserRole.admin;
  }
  
  // Check for reviewer role - use UserRole constants
  if (groups.includes(UserRole.reviewer)) {
    return UserRole.reviewer;
  }
  
  // Check for firm admin role - use UserRole constants
  if (groups.includes(UserRole.firm_admin)) {
    return UserRole.firm_admin;
  }
  
  // Check for firm user role - use UserRole constants
  if (groups.includes(UserRole.firm_user)) {
    return UserRole.firm_user;
  }
  
  return undefined;
}

/**
 * AuthProvider manages authentication state for the entire application.
 * It provides a single source of truth for authentication to prevent race conditions.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  
  // Initialize password change states from sessionStorage to persist across navigation
  const [requiresNewPassword, setRequiresNewPassword] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('requiresNewPassword') || 'false');
    } catch {
      return false;
    }
  });
  
  const [tempUsername, setTempUsername] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('tempUsername');
    } catch {
      return null;
    }
  });

  const [requiresPasswordReset, setRequiresPasswordReset] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('requiresPasswordReset') || 'false');
    } catch {
      return false;
    }
  });

  const [resetUsername, setResetUsername] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('resetUsername');
    } catch {
      return null;
    }
  });

  const refreshUser = useCallback(async (skipLoadingState: boolean = false): Promise<void> => {
    if (!skipLoadingState) {
      setIsLoading(true);
    }
    try {
      // 1. Fetch the session first to get the ID token.
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken;

      if (!idToken) {
        throw new Error('No ID token found in session.');
      }

      // 2. Extract all claims directly from the ID token.
      const payload = idToken.payload;
      
      // Safely extract groups with proper type checking
      const rawGroups = payload['cognito:groups'];
      const groups = Array.isArray(rawGroups) && rawGroups.every(g => typeof g === 'string') 
        ? rawGroups as string[] 
        : [];
      
      // Parse firmIds - check both 'firmIds' and 'custom:firmIds' locations
      const firmIdsRaw = payload.firmIds || payload['custom:firmIds'];
      let firmIds: string[] = [];
      if (firmIdsRaw) {
        // Expect only string format (JSON stringified array) as set by backend
        if (typeof firmIdsRaw === 'string') {
          try {
            firmIds = JSON.parse(firmIdsRaw);
          } catch (e) {
            console.warn('Failed to parse firmIds from token:', e);
            console.warn('Raw firmIds value:', firmIdsRaw);
          }
        } else {
          console.warn('Unexpected firmIds format: expected string but got', typeof firmIdsRaw);
        }
      } else {
        console.warn('No firmIds found in token payload');
      }

      const role = mapGroupsToRole(groups);

      // 3. Fetch remaining user attributes.
      const attributes = await fetchUserAttributes();

      const userData: User = {
        userId: payload.sub as string,
        email: attributes.email || '',
        given_name: attributes.given_name,
        family_name: attributes.family_name,
        role,
        groups,
        firmIds,
        invitedBy: attributes['custom:invitedBy'],
      };

      setUser(userData);
      setSentryUser({
        userId: userData.userId,
        email: userData.email,
        role: userData.role,
        firmIds: userData.firmIds,
      });
    } catch (error: any) {
      // Only log out the user if the error is explicitly an auth error
      if (error.name === 'NotAuthorizedException') {
        setUser(null);
      }
      console.error('Failed to refresh user session:', error);
    } finally {
      if (!skipLoadingState) {
        setIsLoading(false);
      }
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSessionExpired(false); // Clear session expired flag on new login attempt
    // Don't clear requiresNewPassword here - let the actual result determine it
    try {
      // Check if user is already signed in with a valid session
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          // Verify the session is still valid by refreshing
          const session = await fetchAuthSession({ forceRefresh: true });
          if (session.tokens?.idToken) {
            await refreshUser(true);
            setIsLoading(false);
            return;
          }
          // Session is stale (e.g., password was reset) — sign out and proceed with fresh login
          try { await signOut(); } catch (e) { console.warn('Ignoring signOut error for stale session:', e); }
        }
      } catch {
        // No current user or stale session, proceed with login
        try { await signOut(); } catch (e) { console.warn('Ignoring signOut error during login cleanup:', e); }
      }
      
      const result = await signIn({
        username: email,
        password: password,
      });
      
      // Check if new password is required
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setRequiresNewPassword(true);
        setTempUsername(email);
        // Persist to sessionStorage to survive navigation
        sessionStorage.setItem('requiresNewPassword', 'true');
        sessionStorage.setItem('tempUsername', email);
        setIsLoading(false);
        return;
      }
      
      // Clear password change / reset states on successful login
      setRequiresNewPassword(false);
      setTempUsername(null);
      setRequiresPasswordReset(false);
      setResetUsername(null);
      // Clear from sessionStorage
      sessionStorage.removeItem('requiresNewPassword');
      sessionStorage.removeItem('tempUsername');
      sessionStorage.removeItem('requiresPasswordReset');
      sessionStorage.removeItem('resetUsername');
      await refreshUser(true); // Skip loading state in refreshUser since we're managing it here
      setIsLoading(false);
    } catch (error: any) {
      console.error('Login error in AuthProvider:', error);
      
      // Handle Cognito-specific errors
      let errorMessage = 'Failed to sign in';
      
      if (error?.name === 'PasswordResetRequiredException' || error?.message?.includes('Password reset required')) {
        setRequiresPasswordReset(true);
        setResetUsername(email);
        sessionStorage.setItem('requiresPasswordReset', 'true');
        sessionStorage.setItem('resetUsername', email);
        setIsLoading(false);
        return;
      } else if (error?.name === 'NotAuthorizedException' || error?.message?.includes('Incorrect username or password')) {
        errorMessage = 'Incorrect username or password';
      } else if (error?.name === 'UserNotFoundException') {
        errorMessage = 'User does not exist';
      } else if (error?.name === 'UserNotConfirmedException') {
        errorMessage = 'User is not confirmed. Please check your email for confirmation link.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [refreshUser]);
  
  const confirmNewPassword = useCallback(async (newPassword: string, userAttributes?: Record<string, string>): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      
      // In Amplify v6, for NEW_PASSWORD_REQUIRED, the challengeResponse should be the new password
      // and user attributes should be passed under options.userAttributes
      const confirmSignInInput: any = {
        challengeResponse: newPassword
      };
      
      // Add user attributes if provided - they need to be under options
      if (userAttributes && Object.keys(userAttributes).length > 0) {
        confirmSignInInput.options = {
          userAttributes: userAttributes
        };
      }
      
      await confirmSignIn(confirmSignInInput);
      
      setRequiresNewPassword(false);
      setTempUsername(null);
      // Clear from sessionStorage
      sessionStorage.removeItem('requiresNewPassword');
      sessionStorage.removeItem('tempUsername');
      await refreshUser(true);
      setIsLoading(false);
    } catch (error: any) {
      console.error('Password change error:', error);
      let errorMessage = 'Failed to update password';
      
      // Handle specific error cases
      if (error?.message?.includes('given_name is missing')) {
        errorMessage = 'Please provide your first name to complete setup';
      } else if (error?.message?.includes('family_name is missing')) {
        errorMessage = 'Please provide your last name to complete setup';
      } else if (error?.message?.includes('Invalid attributes given')) {
        errorMessage = 'Please provide all required information to complete setup';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [refreshUser]);

  // Helper function to clear all user session state
  const clearUserSession = useCallback(() => {
    clearSentryUser();
    setUser(null);
    setError(null);
    setRequiresNewPassword(false);
    setTempUsername(null);
    setRequiresPasswordReset(false);
    setResetUsername(null);
    sessionStorage.removeItem('requiresNewPassword');
    sessionStorage.removeItem('tempUsername');
    sessionStorage.removeItem('requiresPasswordReset');
    sessionStorage.removeItem('resetUsername');
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      // Clear all local state first
      clearUserSession();

      // Sign out from Amplify (this clears tokens and session)
      await signOut({ global: true });

      // Don't force a reload - let React Router handle navigation
      // The ProtectedRoute component will automatically redirect to /login
      // when isAuthenticated becomes false
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if signOut fails, clear local state
      clearUserSession();
    }
  }, [clearUserSession]);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await updatePassword({
        oldPassword,
        newPassword,
      });
      setIsLoading(false);
    } catch (error: any) {
      console.error('Password change error:', error);
      let errorMessage = 'Failed to change password';
      
      if (error?.name === 'NotAuthorizedException') {
        errorMessage = 'Current password is incorrect';
      } else if (error?.name === 'InvalidPasswordException') {
        errorMessage = 'New password does not meet requirements';
      } else if (error?.name === 'LimitExceededException') {
        errorMessage = 'Too many attempts. Please try again later';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setIsLoading(false);
      throw error; // Re-throw so the calling component can handle success/failure
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleSessionExpired = useCallback(() => {
    // Mark session as expired
    setSessionExpired(true);
    // Clear user state
    clearUserSession();
  }, [clearUserSession]);

  // Initialize authentication on app start
  useEffect(() => {
    // Skip initial refresh if we're in password change flow
    // This prevents "No ID token found" errors during CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED
    const initializeAuth = async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: false });
        if (session.tokens?.idToken) {
          // Only refresh if we have a valid session
          await refreshUser();
        } else {
          // No valid session - stop loading but don't error
          // Don't reset password change states if they exist
          setIsLoading(false);
        }
      } catch (error) {
        // No session available - this is normal during password change flow
        // Don't reset password change states if they exist
        setIsLoading(false);
      }
    };
    
    initializeAuth();
  }, [refreshUser]);

  const contextValue: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    requiresNewPassword,
    tempUsername,
    sessionExpired,
    requiresPasswordReset,
    resetUsername,
    login,
    confirmNewPassword,
    changePassword,
    logout,
    refreshUser,
    clearError,
    handleSessionExpired,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use the AuthContext. Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
